<template>
  <div>
    <v-card class="mx-4 px-4 py-4">
      <v-card-title primary-title>
        <span class="headline">Website Management</span>
      </v-card-title>

      <v-tabs v-model="activeTab" background-color="primary" dark>
        <v-tab key="school">School Info</v-tab>
        <v-tab key="profile">Profile</v-tab>
        <v-tab key="settings">Settings</v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <!-- School Info Tab -->
        <v-tab-item key="school">
          <v-form @submit.prevent="updateSchool">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.school.schname"
                  label="School Name"
                  :disabled="!isAdmin"
                  :rules="[rules.required]"
                  required
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.school.emis"
                  label="EMIS Number"
                  :disabled="!isAdmin"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.school.domain"
                  label="Domain"
                  type="url"
                  :disabled="!isAdmin"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.school.sch_tel"
                  label="Phone Number"
                  type="tel"
                  :disabled="!isAdmin"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="form.school.district"
                  label="District"
                  :disabled="!isAdmin"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="form.school.circuit"
                  label="Circuit"
                  :disabled="!isAdmin"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="form.school.quintile"
                  :items="['1', '2', '3', '4', '5']"
                  label="Quintile"
                  :disabled="!isAdmin"
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.school.sch_address"
                  label="Postal Address"
                  :disabled="!isAdmin"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.school.physical_address"
                  label="Physical Address"
                  :disabled="!isAdmin"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.school.city"
                  label="City"
                  :disabled="!isAdmin"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.school.pcode"
                  label="Postal Code"
                  :disabled="!isAdmin"
                ></v-text-field>
              </v-col>

              <!-- Banking -->
              <v-col cols="12"><h5>Banking Details</h5></v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="form.school.bank"
                  label="Bank"
                  :disabled="!isAdmin"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="form.school.account_number"
                  label="Account Number"
                  :disabled="!isAdmin"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="form.school.branch_code"
                  label="Branch Code"
                  :disabled="!isAdmin"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="form.school.account_type"
                  :items="[
                    'Cheque Account',
                    'Savings Account',
                    'Transmission Account',
                  ]"
                  label="Account Type"
                  :disabled="!isAdmin"
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.school.reference"
                  label="Payment Reference"
                  placeholder="Student ID [eg. 12-345-67]"
                  :disabled="!isAdmin"
                ></v-text-field>
              </v-col>

              <!-- Emails -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.school.admin_email"
                  label="Admin Email"
                  type="email"
                  :disabled="!isAdmin"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.school.finance_email"
                  label="Finance Email"
                  type="email"
                  :disabled="!isAdmin"
                ></v-text-field>
              </v-col>

              <!-- Logo Upload -->
              <v-col cols="12">
                <label>School Logo</label>
                <v-file-input
                  v-if="isAdmin"
                  accept="image/*"
                  prepend-icon="mdi-image"
                  label="Upload Logo"
                  @change="handleLogoUpload"
                ></v-file-input>
                <v-img
                  v-if="form.school.logo"
                  :src="getLogoUrl()"
                  max-width="150"
                  class="mt-2"
                ></v-img>
              </v-col>
            </v-row>

            <v-btn
              v-if="isAdmin"
              color="primary"
              class="mt-4"
              :loading="loading"
              type="submit"
            >
              Update School Info
            </v-btn>
          </v-form>
        </v-tab-item>

        <!-- Profile Tab -->
        <v-tab-item key="profile">
          <v-form @submit.prevent="updateSchool">
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="form.school_profile.site_name"
                  label="Site Name"
                  :disabled="!isAdmin"
                  required
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="form.school_profile.motto"
                  label="School Motto"
                  :disabled="!isAdmin"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="form.school_profile.short_description"
                  label="Short Description"
                  rows="3"
                  :disabled="!isAdmin"
                ></v-textarea>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="form.school_profile.welcome"
                  label="Welcome Message"
                  rows="4"
                  :disabled="!isAdmin"
                ></v-textarea>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="form.school_profile.biography"
                  label="Biography"
                  rows="4"
                  :disabled="!isAdmin"
                ></v-textarea>
              </v-col>

              <v-col cols="12">
                <label>About Us</label>
                <v-textarea
                  v-model="form.school_profile.about_us"
                  row="5"
                  label="About Us"
                  :disabled="!isAdmin"
                >
                </v-textarea>
                <!-- <tinymce-editor
                  v-model="form.school_profile.about_us"
                  :disabled="!isAdmin"
                /> -->
              </v-col>
              <v-col cols="12">
                <label>Vision</label>
                <v-textarea
                  v-model="form.school_profile.vision"
                  row="5"
                  label="Vision"
                  :disabled="!isAdmin"
                >
                </v-textarea>
                <!-- <tinymce-editor
                  v-model="form.school_profile.vision"
                  :disabled="!isAdmin"
                /> -->
              </v-col>
              <v-col cols="12">
                <label>Mission</label>
                <v-textarea
                  v-model="form.school_profile.mission"
                  row="5"
                  label="Mission"
                  :disabled="!isAdmin"
                >
                </v-textarea>
                <!-- <tinymce-editor
                  v-model="form.school_profile.mission"
                  :disabled="!isAdmin"
                /> -->
              </v-col>

              <v-col cols="12">
                <label>Website Banner</label>
                <v-file-input
                  v-if="isAdmin"
                  accept="image/*"
                  label="Upload Banner"
                  @change="handleBannerUpload"
                ></v-file-input>
                <v-img
                  v-if="form.school_profile.website_banner"
                  :src="getBannerUrl()"
                  max-width="300"
                  class="mt-2"
                ></v-img>
              </v-col>
            </v-row>

            <v-btn
              v-if="isAdmin"
              color="primary"
              class="mt-4"
              :loading="loading"
              type="submit"
            >
              Update Profile
            </v-btn>
          </v-form>
        </v-tab-item>

        <!-- Settings Tab -->
        <v-tab-item key="settings">
          <v-form @submit.prevent="updateSchool">
            <v-row>
              <v-col cols="12">
                <v-select
                  v-model="form.school.industry"
                  :items="[
                    'General',
                    'Technical',
                    'Agricultural',
                    'Arts',
                    'Commercial',
                  ]"
                  label="Industry"
                  :disabled="!isAdmin"
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-color-picker
                  v-model="form.site_settings.primary_bg_color"
                  label="Primary Background Color"
                  :disabled="!isAdmin"
                ></v-color-picker>
              </v-col>
              <v-col cols="12" md="6">
                <v-color-picker
                  v-model="form.site_settings.secondary_bg_color"
                  label="Secondary Background Color"
                  :disabled="!isAdmin"
                ></v-color-picker>
              </v-col>
            </v-row>
            <v-btn
              v-if="isAdmin"
              color="primary"
              class="mt-4"
              :loading="loading"
              type="submit"
            >
              Update Settings
            </v-btn>
          </v-form>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </div>
</template>

<script>
//import TinymceEditor from "@/components/myLibrary/TinyMCE.vue";

export default {
  name: "SchoolManagement",
  components: {
    // TinymceEditor,
  },
  data() {
    return {
      activeTab: "school",
      rules: {
        required: (v) => !!v || "This field is required",
      },
      loading: false,
      errors: {},
      form: {
        school: {
          id: null,
          emis: "",
          schname: "",
          domain: "",
          district: "",
          circuit: "",
          quintile: "",
          sch_address: "",
          city: "",
          physical_address: "",
          sch_tel: "",
          pcode: "",
          logo: "",
          reference: "",
          account_number: "",
          account_type: "",
          bank: "",
          branch_code: "",
          industry: "",
          admin_email: "",
          finance_email: "",
        },
        school_profile: {
          id: null,
          emis: "",
          about_us: "",
          motto: "",
          vision: "",
          biography: "",
          short_description: "",
          mission: "",
          site_name: "",
          welcome: "",
          website_banner: "",
        },
        site_settings: {
          id: null,
          primary_bg_color: "#0000FF",
          secondary_bg_color: "#FFFFFF",
          emis: "",
        },
      },
      editorConfig: {
        height: 300,
        menubar: false,
        plugins: [
          "advlist autolink lists link image charmap print preview anchor",
          "searchreplace visualblocks code fullscreen",
          "insertdatetime media table paste code help wordcount",
        ],
        toolbar:
          "undo redo | formatselect | bold italic backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help",
      },
    };
  },
  computed: {
    getUserSchool() {
      return this.$store.getters.getUserSchool;
    },
    isAdmin() {
      return this.$store.getters.isAdmin;
    },
  },
  watch: {
    getUserSchool: {
      handler(newVal) {
        if (newVal) {
          this.populateForm();
        }
      },
      immediate: true,
      deep: true,
    },
  },
  mounted() {
    this.populateForm();
  },
  methods: {
    populateForm() {
      const userSchool = this.getUserSchool;

      if (userSchool) {
        // Populate school data
        if (userSchool.school) {
          this.form.school = { ...this.form.school, ...userSchool.school };
        } else {
          // If no nested school object, use root level data
          this.form.school = { ...this.form.school, ...userSchool };
        }

        // Populate school profile data
        if (userSchool.school_profile) {
          this.form.school_profile = {
            ...this.form.school_profile,
            ...userSchool.school_profile,
          };
        } else {
          // Use root level data for profile fields
          this.form.school_profile = {
            ...this.form.school_profile,
            about_us: userSchool.about_us || "",
            motto: userSchool.motto || "",
            vision: userSchool.vision || "",
            biography: userSchool.biography || "",
            short_description: userSchool.short_description || "",
            mission: userSchool.mission || "",
            site_name: userSchool.site_name || "",
            welcome: userSchool.welcome || "",
          };
        }

        // Populate site settings data
        if (userSchool.site_settings) {
          this.form.site_settings = {
            ...this.form.site_settings,
            ...userSchool.site_settings,
          };
        } else {
          // Use root level data for settings
          this.form.site_settings = {
            ...this.form.site_settings,
            primary_bg_color: userSchool.primary_bg_color || "#0000FF",
            secondary_bg_color: userSchool.secondary_bg_color || "#FFFFFF",
          };
        }
      }
    },

    async updateSchool() {
      if (!this.isAdmin) {
        this.$toast.error(
          "You do not have permission to update school information",
          { duration: 5000 }
        );
        return;
      }

      this.loading = true;
      this.errors = {};

      try {
        // Basic validation
        if (!this.validateForm()) {
          this.loading = false;
          return;
        }

        // Create FormData for file uploads
        const formData = new FormData();

        // Append all form data
        formData.append("school", JSON.stringify(this.form.school));
        formData.append(
          "school_profile",
          JSON.stringify(this.form.school_profile)
        );
        formData.append(
          "site_settings",
          JSON.stringify(this.form.site_settings)
        );

        // Append files if they exist
        if (this.$refs.logoInput && this.$refs.logoInput.files[0]) {
          formData.append("logo", this.$refs.logoInput.files[0]);
        }
        if (this.$refs.bannerInput && this.$refs.bannerInput.files[0]) {
          formData.append("website_banner", this.$refs.bannerInput.files[0]);
        }

        const response = await this.$http.post("/api/school/update", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        // Update store with new data
        this.$store.commit("setUserSchool", response.data.data);

        this.$toast.success("School information updated successfully!", {
          duration: 5000,
        });
      } catch (error) {
        console.error("Update error:", error);

        if (error.response && error.response.data.errors) {
          this.errors = error.response.data.errors;
          this.$toast.error("Please check the form for errors", {
            duration: 5000,
          });
        } else {
          this.$toast.error(
            "Failed to update school information. Please try again.",
            { duration: 5000 }
          );
        }
      } finally {
        this.loading = false;
      }
    },

    validateForm() {
      this.errors = {};
      let isValid = true;

      // Required field validation
      if (!this.form.school.schname.trim()) {
        this.errors.schname = "School name is required";
        isValid = false;
      }

      if (!this.form.school_profile.site_name.trim()) {
        this.errors.site_name = "Site name is required";
        isValid = false;
      }

      // Email validation
      if (
        this.form.school.admin_email &&
        !this.isValidEmail(this.form.school.admin_email)
      ) {
        this.errors.admin_email = "Please enter a valid email address";
        isValid = false;
      }

      if (
        this.form.school.finance_email &&
        !this.isValidEmail(this.form.school.finance_email)
      ) {
        this.errors.finance_email = "Please enter a valid email address";
        isValid = false;
      }

      // URL validation
      if (
        this.form.school.domain &&
        !this.isValidUrl(this.form.school.domain)
      ) {
        this.errors.domain = "Please enter a valid URL";
        isValid = false;
      }

      return isValid;
    },

    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },

    isValidUrl(url) {
      try {
        new URL(url);
        return true;
      } catch {
        return false;
      }
    },

    handleLogoUpload(event) {
      const file = event.target.files[0];
      if (file) {
        // Validate file size (2MB max)
        if (file.size > 2 * 1024 * 1024) {
          this.$toast.error("Logo file size must be less than 2MB", {
            duration: 5000,
          });
          event.target.value = "";
          return;
        }

        // Validate file type
        if (!file.type.startsWith("image/")) {
          this.$toast.error("Please select a valid image file", {
            duration: 5000,
          });
          event.target.value = "";
          return;
        }
      }
    },

    handleBannerUpload(event) {
      const file = event.target.files[0];
      if (file) {
        // Validate file size (5MB max)
        if (file.size > 5 * 1024 * 1024) {
          this.$toast.error("Banner file size must be less than 5MB", {
            duration: 5000,
          });
          event.target.value = "";
          return;
        }

        // Validate file type
        if (!file.type.startsWith("image/")) {
          this.$toast.error("Please select a valid image file", {
            duration: 5000,
          });
          event.target.value = "";
          return;
        }
      }
    },

    getLogoUrl() {
      if (this.form.school.logo) {
        // Assuming logos are stored in a specific directory
        return `/storage/logos/${this.form.school.logo}`;
      }
      return null;
    },

    getBannerUrl() {
      if (this.form.school_profile.website_banner) {
        // Assuming banners are stored in a specific directory
        return `/storage/banners/${this.form.school_profile.website_banner}`;
      }
      return null;
    },
  },
};
</script>

<style scoped>
.color-preview {
  width: 100px;
  height: 40px;
  text-align: center;
  line-height: 40px;
  border: 1px solid #ccc;
  border-radius: 4px;
}
.nav-tabs .nav-link {
  cursor: pointer;
  color: #495057;
  border: 1px solid transparent;
  border-top-left-radius: 0.25rem;
  border-top-right-radius: 0.25rem;
}

.nav-tabs .nav-link:hover {
  border-color: #e9ecef #e9ecef #dee2e6;
}

.nav-tabs .nav-link.active {
  color: #495057;
  background-color: #fff;
  border-color: #dee2e6 #dee2e6 #fff;
}

.tab-content {
  border: 1px solid #dee2e6;
  border-top: none;
  padding: 1.5rem;
  background-color: #fff;
}

.disabled-editor {
  pointer-events: none;
  opacity: 0.6;
}

.color-preview {
  width: 100px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #dee2e6;
  color: white;
  font-weight: bold;
  text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.5);
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.img-thumbnail {
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  padding: 0.25rem;
}

.form-control:disabled {
  background-color: #e9ecef;
  opacity: 1;
}

.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid rgba(0, 0, 0, 0.125);
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid rgba(0, 0, 0, 0.125);
}

.text-danger {
  color: #dc3545 !important;
}

.is-invalid {
  border-color: #dc3545;
}

.invalid-feedback {
  display: block;
  width: 100%;
  margin-top: 0.25rem;
  font-size: 0.875em;
  color: #dc3545;
}

@media (max-width: 768px) {
  .tab-content {
    padding: 1rem;
  }

  .color-preview {
    width: 80px;
    height: 35px;
    font-size: 0.8rem;
  }
}
</style>
